import React from 'react'
import { useTable, useFilters, useGlobalFilter, useAsyncDebounce, useSortBy } from 'react-table'
import { SortIcon, SortUpIcon, SortDownIcon } from './shared/Icons'

// Highlight function
function highlightText(text, filter) {
  if (!filter || !text || typeof text !== 'string') return text;
  
  const regex = new RegExp(`(${filter})`, 'gi');
  return text.split(regex).map((part, index) => 
    regex.test(part) ? (
      <mark key={index} className="bg-yellow-200">{part}</mark>
    ) : (
      part
    )
  );
}


function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter || '');
  
  const onChange = useAsyncDebounce(value => {
    const url = new URL(window.location.href);
    const hash = url.hash;
    
    // 검색어 업데이트
    if (value) {
      url.searchParams.set('q', value);
    } else {
      url.searchParams.delete('q');
    }
    
    // 해시 유지하면서 URL 업데이트
    window.history.pushState({}, '', `${url.pathname}${url.search}${hash}`);
    
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <div>
      <label className="flex gap-x-2 items-baseline">
        <span className="text-theme-gray">키워드 검색: </span>
        <input
          type="text"
          name="search_term_string"
          className="rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          value={value}
          onChange={e => {
            setValue(e.target.value);
            onChange(e.target.value);
          }}
          placeholder={`${count} 개의 항목 검색...`}
        />
      </label>
    </div>
  );
}

export function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id, render },
}) {
  const options = React.useMemo(() => {
    const options = new Set()
    preFilteredRows.forEach(row => {
      options.add(row.values[id])
    })
    return [...options.values()]
  }, [id, preFilteredRows])

  return (
    <label className="flex gap-x-2 items-baseline">
      <span className="text-theme-gray">{render("Header")}: </span>
      <select
        className="rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
        name={id}
        id={id}
        value={filterValue}
        onChange={e => {
          setFilter(e.target.value || undefined)
        }}
      >
        <option value="">전체</option>
        {options.map((option, i) => (
          <option key={i} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  )
}

export function CustomTitle({ value, globalFilter }) {
  const titles = value.split('\n');

  return (
    <div key={`${value}_customTitle`}>
      <span className="font-bold text-theme-headerrow">
        {highlightText(titles[0], globalFilter)}
      </span>
      <br></br>
      <span className="text-xs font-semibold text-theme-gray">
        {highlightText(titles[1], globalFilter)}
      </span>
      <br></br>
      <span className="text-xs font-semibold text-theme-gray">
        {highlightText(titles[2], globalFilter)}
      </span>
    </div>
  );
}

export function CustomOpenSector({ value, globalFilter }) {
  const titles = value.split('\n');

  return (
    <div key={`${value}__customOpenSector`} className="text-theme-gray">
    {titles.map(element => 
      <div key={element}>
        <span className="text-theme-gray font-semibold"> 
          {highlightText(element, globalFilter)}
        </span>
        <br></br>
      </div>
    )}
    </div>
  );
}

export function CustomTier({ value, globalFilter }) {
  const titles = value.split('\n');

  return (
    <div className="text-theme-gray">
      <div key={`${titles[0]}_customTier`}>
        <span className="text-theme-yellow text-sm font-semibold"> 
          {highlightText(titles[0], globalFilter)}
        </span>
      </div>
      {
        titles.slice(1).map((x, i) => 
          <div key={`${x}_customTier_child`}>
            <span className="text-theme-gray text-xs font-semibold"> 
              {highlightText(x, globalFilter)}
            </span>
          </div>
        )
      }

    </div>
  );
}

function Table({ columns, data }) {
  const [isInitializing, setIsInitializing] = React.useState(true);
  
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        // URL에서 검색어만 추출
        globalFilter: new URLSearchParams(
          window.location.search.split('#')[0]
        ).get('q') || ''
      }
    },
    useFilters,
    useGlobalFilter,
    useSortBy
  )

  // 초기화 및 URL 처리
  React.useEffect(() => {
    // URL에서 검색어와 해시 분리
    const [urlPath, hash] = window.location.href.split('#');
    const searchQuery = new URLSearchParams(urlPath.split('?')[1]).get('q');
    
    if (searchQuery) {
      setGlobalFilter(searchQuery);
    }
    
    // 해시 처리 (기존 코드 유지)
    if (hash) {
      const locationName = decodeURIComponent(hash.replace('location-', ''));
      const element = document.querySelector(`[data-location="${locationName}"]`);
      
      if (element) {
        const isAltRow = element.classList.contains("bg-theme-altrow");
        
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
          
          if(isAltRow){
            element.classList.remove('bg-theme-altrow');
          }
          
          element.classList.add('bg-yellow-100');
          
          setTimeout(() => {
            element.classList.remove('bg-yellow-100');
            if(isAltRow){
              element.classList.add('bg-theme-altrow');
            }
          }, 2000);
        }, 500); // 검색 필터가 적용된 후 스크롤하도록 약간의 지연 추가
      }
    }

    setTimeout(() => setIsInitializing(false), 100);
  }, []);

  // 행 공유 함수
  const shareRow = (locationID) => {
    const url = `${window.location.origin}${window.location.pathname}#location-${locationID}`;
    navigator.clipboard.writeText(url).then(() => {
      alert('URL이 클립보드에 복사되었습니다!');
    });
  };

  return (
    <div className={`transition-opacity duration-300 ${isInitializing ? 'opacity-0' : 'opacity-100'}`}>
      <div className="sm:flex sm:gap-x-2">
        {headerGroups.map((headerGroup) =>
          headerGroup.headers.map((column) =>
            column.Filter ? (
              <div className="mt-2 sm:mt-0" key={column.id}>
                {column.render("Filter")}
              </div>
            ) : null
          )
        )}
        <GlobalFilter
          preGlobalFilteredRows={preGlobalFilteredRows}
          globalFilter={state.globalFilter}
          setGlobalFilter={setGlobalFilter}
        />
      </div>
      
      {/* 검색 결과 요약 추가 */}
      {state.globalFilter && (
        <div className="mt-2 text-sm text-gray-600">
          검색어 "{state.globalFilter}"에 대한 결과: {rows.length}건
        </div>
      )}

      <div className="mt-4 flex flex-col">
        <div className="-my-2 overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-2xl">
              <table {...getTableProps()} className=" bg-theme-headerrow min-w-full divide-y divide-gray-200">
                <thead className="bg-theme-headerrow">
                  {headerGroups.map(headerGroup => ( 
                    <tr  {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map(column => (
                        <th
                          scope="col"
                          className="group px-6 py-3 text-left text-xl pt-4  tracking-wider font-medium bg-theme-headerrow text-theme-headerwhite"
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                        >
                          <div className="flex items-center justify-between">
                            {column.render('Header')}
                            <span>
                              {column.isSorted
                                ? column.isSortedDesc
                                  ? <SortDownIcon className="w-4 h-4 text-theme-headerwhite" />
                                  : <SortUpIcon className="w-4 h-4 text-theme-headerwhite" />
                                : (
                                  <SortIcon className="w-4 h-4 text-theme-headerwhite opacity-0 group-hover:opacity-100" />
                                )}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody
                  {...getTableBodyProps()}
                  className="bg-white divide-y divide-gray-200"
                >
                  {rows.map((row, i) => {  
                    prepareRow(row)
                    const locationID = row.original.ID || ''; // title 컬럼에 해역명이 있다고 가정
                    return (
                      <tr 
                        {...row.getRowProps()} 
                        className={i % 2 === 0 ? "hover:bg-theme-hover bg-theme-altrow" : "hover:bg-theme-hover"} 
                        data-location={locationID}
                      >
                        {row.cells.map(cell => {
                          return (
                            <td
                              {...cell.getCellProps()}
                              className={"px-6 py-4 whitespace-nowrap content-start"}
                              role="cell"
                            > 
                              {cell.column.Cell.name === "defaultRenderer"
                                ? <div className="text-sm text-theme-gray">{cell.render('Cell')}</div>
                                : cell.render('Cell', { globalFilter: state.globalFilter })}
                            </td>
                          )
                        })}
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => shareRow(locationID)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            공유
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Table;